import React, { useState } from 'react';
import PropTypes from 'prop-types';

import TicketResource from 'resources/organization/TicketResource';

import ActionButtons from './ActionButtons';

import Button from 'ui/Button';

import ConfirmModal from 'components/ConfirmModal';
import EmailEditor from "components/EmailEditor";
import EmailConfirmModalContent from "components/EmailConfirmModalContent";

import formattedDate from 'utils/formattedDate';
import isMobileDevice from 'utils/mobileAndTabletCheck';
import useEndSalesController from 'utils/useEndSalesController';
import { formatPrice } from 'utils/rewards';
import { useError } from 'utils/useErrorController';
import { useEmailController } from "utils/useEmailController";

import { Avatar, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import moment from 'moment';

const ListItemComponent = ({ event, organizationId, loading, history, onDelete, onCheckin, onDuplicate, onNewTicket }) => {

  const [confirmEmailSend, setConfirmEmailSend] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const {title, status, start_date, has_ticket, event_products, issued_tickets_count, ticket_revenue, product_revenue, currency, calculated_capacity} = event;

  const transformedTitle = `${title?.slice(0, 40)}${title?.length > 40 ? "..." : ""}`;
  
  const listItemStyle = {
    backgroundColor: "#ffff", 
    margin: '16px 0px', 
    borderRadius: 'var(--radius)', 
    display: 'flex',
    justifyContent: 'space-between',
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const isMobile = isMobileDevice();

  const eventDate = moment(start_date).utc().startOf('day');
  const today = moment().utc().startOf('day');
  // No has_ticket condition: ticketless events use the check-in page to
  // collect merchandise, so the badge shows for any published event today.
  const showCheckInButton = eventDate.isSame(today) && status === 'published';
  const hasCheckInTargets = has_ticket || event_products?.length > 0;

  const {
    email,
    setEmail,
    editingEmail,
    handleEmailSendRequest,
    handleEmailSend,
    handleEditEmailClick,
    handleEmailCancel,
  } = useEmailController(
    organizationId,
    setConfirmEmailSend,
    event.id,
    sessionId,
  );

  // End-of-sale targets: every sellable listing, regardless of remaining
  // availability — ending a sold-out listing still releases its reserved
  // stock. The API rejects merch end-sales for recurring events (stock is
  // shared across the parent-event window), so only single events offer them.
  const endableProducts = event.single ? (event_products || []).filter((product) => product.sellable) : [];

  // Mirrors what the modal will actually list: available tickets (per session
  // for recurring events) plus the endable products above. Distinct from
  // hasCheckInTargets — a recurring merch-only event is checkin-able but has
  // nothing to end the sale of.
  const availableTickets = event.single
    ? (event.tickets || []).filter((ticket) => ticket.available)
    : (event.sessions || []).flatMap((session) => session.tickets || []).filter((ticket) => ticket.available);
  const hasEndSaleTargets = availableTickets.length > 0 || endableProducts.length > 0;

  const {
    openEndSalesModal,
    EndSalesModalComponent,
    handleEndSalesModalOpen
  } = useEndSalesController(
    event,
    TicketResource,
    {organization_id: organizationId, event_id: event.id},
    {
      products: endableProducts,
      itemNoun: endableProducts.length > 0 ? "item" : "ticket",
    }
  );

  const {handleError} = useError();

  const ticketCount = sessionId ? event.sessions.find(session => session.id === sessionId).issued_tickets_count : event.issued_tickets_count;
  
  return (
    <>
      <ListItem 
        alignItems="flex-start"
        sx={{...listItemStyle, flexDirection: isMobile ? 'column' : 'row' }} >

        <div style={containerStyle}>

          <Avatar variant="rounded" src={event.image_url} />

          <ListItemText 
            primary={transformedTitle} 
            secondary={formattedDate(event.start_date, 'date')} />
            
        </div>

        <div style={{...containerStyle, alignSelf: isMobile ? 'flex-end' : 'center'}}>

          {showCheckInButton &&
            <Button 
                small 
                className={"chip-like"}
                icon={LibraryAddOutlinedIcon}
                onClick={() => history.push(`/organizations/${organizationId}/events/${event.id}/tickets`)}
                disabled={loading || !hasCheckInTargets}>
                    Check In
            </Button>
          }

          {status === 'published' &&
              <ListItemText
                sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 'fit-content'}}
                primary={
                  <Tooltip title="Tickets sold / capacity · ticket revenue" placement="left" arrow>
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
                      {`${issued_tickets_count} / ${calculated_capacity}`}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {formatPrice(ticket_revenue, currency, true)}
                      </Typography>
                      <ConfirmationNumberOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
                    </span>
                  </Tooltip>
                }
                secondary={
                  <Tooltip title="Product revenue — event merch and store items" placement="left" arrow>
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
                      {formatPrice(product_revenue, currency, true)}
                      <StorefrontOutlinedIcon sx={{fontSize: 16}} />
                    </span>
                  </Tooltip>
                } />
          }

          <ActionButtons
            event={event}
            loading={loading}
            organizationId={organizationId}
            hasEndSaleTargets={hasEndSaleTargets}
            history={history}
            handleConfirmDeleteModal={onDelete}
            openCheckinCredentialsModal={onCheckin}
            openEmailModal={handleEditEmailClick}
            openEndSalesModal={handleEndSalesModalOpen}
            onDuplicate={onDuplicate}
            onNewTicket={onNewTicket}
          />

        </div>
      </ListItem>

      {editingEmail && (
        <EmailEditor
          object={email}
          onObjectChange={setEmail}
          onEmailSendRequest={handleEmailSendRequest}
          onEmailCancel={handleEmailCancel}
          onError={handleError}
          autoSelect={true}
          event={event}
          onSessionSelect={setSessionId}
        />
      )}

      {confirmEmailSend && (
        <ConfirmModal
          title={"Send email?"}
          open={confirmEmailSend}
          setOpen={setConfirmEmailSend}
          onConfirm={handleEmailSend}
        >
          {
            <EmailConfirmModalContent
              content={email?.content}
              subject={email?.subject}
              ticketCount={ticketCount}
            />
          }
        </ConfirmModal>
      )}

      {openEndSalesModal && 
        <EndSalesModalComponent />}
    </>
  );
};

ListItemComponent.propTypes = {
  event: PropTypes.object,
  organizationId: PropTypes.number,
  loading: PropTypes.bool,
  history: PropTypes.object,
  onDelete: PropTypes.func,
  onCheckin: PropTypes.func,
  onEmailSendRequest: PropTypes.func,
  onDuplicate: PropTypes.func,
  onNewTicket: PropTypes.func
};

export default ListItemComponent;
